import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CreditInformationResponse } from "../../service/contract/approval";
import { SelectedApplianceConfig } from "../../pages/customer/get-solar/components/energy-requirements/energy-requirements.types";
import { RecommendationDetails, StoreProductDetailsResponse, getEventTitle, getSolarRecommendationEventDescription } from "../../service/contract/solar-recommendation";

export interface InstallationData {
	canAuthorize: boolean;
	electricitySupply: string;
	installationAddress: string;
}

export interface PaymentPlanDetailsData {
	alternatePowerExpense: number;
	timelineType: number;
	solarMonthlyRepayment: number | null;
	paymentPeriod: number | null;
	oneTimePaymentAmount: number | null;
}

interface GetSolarState {
	paymentPlan: number | null;
	paymentPlanDetails: PaymentPlanDetailsData | null;
	creditInfo: CreditInformationResponse | null;
	appliances: SelectedApplianceConfig[];
	installation: InstallationData | null;
	recommendationDetails: RecommendationDetails | null;
	selectedProduct: StoreProductDetailsResponse | null;
}

const initialState: GetSolarState = {
	paymentPlan: null,
	paymentPlanDetails: null,
	creditInfo: null,
	appliances: [],
	installation: null,
	recommendationDetails: null,
	selectedProduct: null,
};

export const getSolarSlice = createSlice({
	name: "getSolar",
	initialState,
	reducers: {
		setPaymentPlan: (state, action: PayloadAction<number>) => {
			state.paymentPlan = action.payload;
		},
		setPaymentPlanDetails: (state, action: PayloadAction<PaymentPlanDetailsData | null>) => {
			state.paymentPlanDetails = action.payload;
		},
		resetGetSolar: () => initialState,
		setCreditInfo: (state, action: PayloadAction<CreditInformationResponse | null>) => {
			state.creditInfo = action.payload;
		},
		setAppliances: (state, action: PayloadAction<SelectedApplianceConfig[]>) => {
			state.appliances = action.payload;
		},
		setInstallation: (state, action: PayloadAction<InstallationData>) => {
			state.installation = action.payload;
		},
		setRecommendationDetails: (state, action: PayloadAction<RecommendationDetails | null>) => {
			if (action.payload) {
				state.recommendationDetails = {
					...action.payload,
					events: action.payload.events.map((event) => ({
						...event,
						eventTitle: getEventTitle(event.type, event.typeOfEvent),
						eventDescription: getSolarRecommendationEventDescription(event.type, event.typeOfEvent),
					})),
				};
			} else {
				state.recommendationDetails = null;
			}
		},
		setSelectedProduct: (state, action: PayloadAction<StoreProductDetailsResponse | null>) => {
			state.selectedProduct = action.payload;
		}
	},
});

export const { setPaymentPlan, setPaymentPlanDetails, resetGetSolar, setCreditInfo, setAppliances, setInstallation, setRecommendationDetails, setSelectedProduct } = getSolarSlice.actions;
export default getSolarSlice.reducer;
